/**
 * Deep-link a coordinate pair into the user's preferred maps app for
 * turn-by-turn navigation. Zero-cost path: no in-app Mapbox / Google Maps SDK,
 * no subscription — we hand off to Apple Maps on iOS and Google Maps
 * everywhere else. Drivers get the same navigation they use every day.
 *
 * Detection order:
 *   1. iOS device → `maps://?daddr=…` (Apple Maps)
 *   2. Android + Google Maps installed → `google.navigation:q=…` (in-app)
 *   3. Fallback (web / desktop) → `https://www.google.com/maps/dir/?api=1&…`
 */

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

/**
 * Open turn-by-turn nav from the driver's current location to the given
 * destination lat/lng. `label` is shown in the maps app as the pin name.
 */
export function openNavigation({ lat, lng, label = "" }) {
  if (lat == null || lng == null) {
    console.warn("[nav] missing coords, skipping deep link");
    return false;
  }
  const q = `${lat},${lng}`;
  const name = encodeURIComponent(label || "Destination");

  let url;
  if (isIOS()) {
    // Apple Maps — `saddr` omitted → uses current location.
    url = `maps://?daddr=${q}&q=${name}`;
  } else if (isAndroid()) {
    // Google Maps in-app turn-by-turn (opens directly in nav mode).
    url = `google.navigation:q=${q}&mode=d`;
  } else {
    // Web / desktop fallback.
    url = `https://www.google.com/maps/dir/?api=1&destination=${q}&travelmode=driving`;
  }

  try {
    window.location.href = url;
    return true;
  } catch (e) {
    console.error("[nav] deep-link failed:", e);
    return false;
  }
}

/**
 * Two-stop trip: nav from `pickup` to `dropoff` with a via-point. Only
 * supported on Google Maps in browsers; on iOS Apple Maps ignores `saddr`
 * so we fall back to a single-destination link.
 */
export function openTripNavigation({ pickup, dropoff }) {
  if (isIOS()) {
    // Apple Maps limitation — start nav to pickup; driver taps again for dropoff.
    return openNavigation({ ...pickup, label: "Pickup" });
  }
  const origin = pickup?.lat != null ? `&origin=${pickup.lat},${pickup.lng}` : "";
  const dest = `${dropoff.lat},${dropoff.lng}`;
  const url = `https://www.google.com/maps/dir/?api=1${origin}&destination=${dest}&travelmode=driving`;
  window.location.href = url;
  return true;
}
